import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { M365User } from "../types";
import { useSort, sortRows, SortableTh } from "./SortableTh";
import { formatDateTime } from "../utils/datetime";
import M365DelegationAudit from "./M365DelegationAudit";

interface MailboxSettings {
  automatic_replies: { status?: string | null; internal_message?: string | null; external_message?: string | null };
  forwarding: { enabled: boolean; forward_to?: string | null; keep_copy: boolean };
  aliases?: { primary?: string | null; aliases: string[] };
}

const AUTOREPLY_STATUSES = ["disabled", "alwaysEnabled", "scheduled"] as const;

const CARD = "bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm";
const INPUT =
  "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm";
const H = "text-sm font-semibold text-gray-800 dark:text-gray-100";
const SUB = "text-xs text-gray-500 dark:text-gray-400";

function errText(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { detail?: string } } };
  return err.response?.data?.detail || fallback;
}

// Alphabetical by the label shown in dropdowns (display name, else UPN).
const byName = (a: M365User, b: M365User) =>
  (a.display_name || a.upn || "").localeCompare(b.display_name || b.upn || "");

/**
 * Exchange management. Mailbox settings (auto-reply / forwarding) go through
 * Graph and work whenever a tenant is connected. Distribution lists, mail-enabled
 * security groups, shared mailboxes, and mailbox delegation are Exchange
 * recipients that require Exchange Online PowerShell (app-only cert) — those
 * sections are gated on the tenant being EXO-provisioned. Scoped by the global
 * customer switcher.
 */
export default function M365MailboxPanel({
  users,
  customerId,
}: {
  users: M365User[];
  customerId: number | null;
}) {
  const [exoConfigured, setExoConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    if (customerId == null) return;
    setExoConfigured(null);
    api
      .get(`/api/v1/m365/${customerId}/exchange/status`)
      .then((r) => setExoConfigured(!!r.data.configured))
      .catch(() => setExoConfigured(false));
  }, [customerId]);

  if (customerId == null) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 italic">
        Select a customer in the header to manage Exchange.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {exoConfigured === false && (
        <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-4 py-3">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            Exchange Online management isn't enabled for this customer yet.
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
            Distribution lists, mail-enabled security groups, shared mailboxes, and mailbox
            delegation need Exchange Online PowerShell. Re-run <strong>Microsoft 365 → Tenants →
            Auto-connect</strong> for this customer — the tool provisions a certificate and grants
            Exchange.ManageAsApp. Mailbox settings below work without it.
          </p>
        </div>
      )}

      <DistributionGroups customerId={customerId} enabled={!!exoConfigured} />
      <SharedMailboxes customerId={customerId} users={users} enabled={!!exoConfigured} />
      {/* The tenant-wide AUDIT sits above the per-mailbox editor on purpose:
          the natural order is find the exposure, then fix it. The editor below
          answers "who has access to THIS mailbox"; this answers "across every
          mailbox, who has access to anything", which is the question an
          access review actually asks and which a dropdown cannot answer. */}
      <M365DelegationAudit customerId={customerId} enabled={!!exoConfigured} />
      <MailboxPermissions customerId={customerId} users={users} enabled={!!exoConfigured} />
      <MailboxSettingsSection customerId={customerId} users={users} />
      <MessageTrace customerId={customerId} enabled={!!exoConfigured} />
    </div>
  );
}

// ── Reusable alias chips (add / remove secondary SMTP addresses via Graph) ────

function AliasManager({
  customerId,
  mailbox,
  primary,
  aliases,
  onChange,
}: {
  customerId: number;
  mailbox: string; // UPN / primary address / object id — the Graph user identity
  primary?: string | null;
  aliases: string[];
  onChange: (next: { primary?: string | null; aliases: string[] }) => void;
}) {
  // Split into local part + domain. It used to be one free-text box expecting a
  // full address, which meant an operator had to already know which domains the
  // tenant accepts — and typing one it does not accept fails at Exchange with a
  // message that does not explain why.
  const [newAlias, setNewAlias] = useState("");
  const [aliasDomains, setAliasDomains] = useState<string[]>([]);
  const [aliasDomain, setAliasDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/api/v1/m365/${customerId}/exchange/accepted-domains`)
      .then((r) => {
        const list: string[] = (r.data || []).map((d: any) => d.DomainName).filter(Boolean);
        setAliasDomains(list);
        const def = (r.data || []).find((d: any) => d.Default)?.DomainName;
        // Prefer the domain the mailbox itself is on — an alias almost always
        // belongs beside the primary address.
        const fromPrimary = (primary || "").split("@")[1];
        setAliasDomain(
          (fromPrimary && list.includes(fromPrimary) && fromPrimary) || def || list[0] || "",
        );
      })
      .catch(() => {
        // Non-fatal: fall back to the free-text box so this still works when
        // Exchange management is not configured.
        setAliasDomains([]);
        setAliasDomain("");
      });
  }, [customerId, primary]);

  const add = async () => {
    const local = newAlias.trim();
    if (!local) return;
    // With a domain picked, the box holds only the local part; without one it
    // still accepts a full address, so nothing regresses when the domain list
    // is unavailable.
    const a = aliasDomain && !local.includes("@") ? `${local}@${aliasDomain}` : local;
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(
        `/api/v1/m365/${customerId}/mailbox/${encodeURIComponent(mailbox)}/aliases`,
        { alias: a },
      );
      setNewAlias("");
      onChange(r.data);
    } catch (e) {
      setErr(errText(e, "Add alias failed"));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (a: string) => {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(
        `/api/v1/m365/${customerId}/mailbox/${encodeURIComponent(mailbox)}/aliases/remove`,
        { alias: a },
      );
      onChange(r.data);
    } catch (e) {
      setErr(errText(e, "Remove alias failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        {primary && (
          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 px-2 py-0.5 rounded-full">
            {primary}
            <span className="opacity-70">primary</span>
          </span>
        )}
        {aliases.map((a) => (
          <span
            key={a}
            className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 px-2 py-0.5 rounded-full"
          >
            {a}
            <button
              onClick={() => remove(a)}
              disabled={busy}
              title="Remove alias"
              className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 leading-none"
            >
              ×
            </button>
          </span>
        ))}
        {!primary && aliases.length === 0 && <span className={SUB}>no aliases</span>}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input
          value={newAlias}
          onChange={(e) => setNewAlias(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={aliasDomains.length ? "new.alias" : "new.alias@domain.com"}
          className={`${INPUT} flex-1`}
        />
        {aliasDomains.length > 0 && (
          <>
            <span className="text-xs text-gray-400 dark:text-gray-500">@</span>
            <select
              value={aliasDomain}
              onChange={(e) => setAliasDomain(e.target.value)}
              className={`${INPUT} w-52`}
              title="Accepted domains for this tenant"
            >
              {aliasDomains.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </>
        )}
        <button
          onClick={add}
          disabled={busy || !newAlias.trim()}
          className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          Add alias
        </button>
      </div>
      {err && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{err}</p>}
    </div>
  );
}

// ── Distribution lists + mail-enabled security groups (EXO) ──────────────────

function DistributionGroups({ customerId, enabled }: { customerId: number; enabled: boolean }) {
  const [rows, setRows] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [alias, setAlias] = useState("");
  const [mailSec, setMailSec] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // The tenant's accepted domains, so the address is a CHOICE rather than
  // whatever Exchange treats as default. Previously there was no domain list at
  // all and a multi-domain tenant simply could not be served.
  const [domains, setDomains] = useState<string[]>([]);
  const [domain, setDomain] = useState("");
  // Inline rename of an existing list's address — there was no way to correct
  // one short of deleting the list and losing its membership.
  const [editing, setEditing] = useState<string | null>(null);
  const [editAddr, setEditAddr] = useState("");
  const [open, setOpen] = useState<string | null>(null); // expanded DL identity
  const [members, setMembers] = useState<any[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [newMember, setNewMember] = useState("");

  const load = useCallback(() => {
    if (!enabled) return;
    setLoading(true);
    api
      .get(`/api/v1/m365/${customerId}/exchange/distribution-groups`)
      .then((r) => setRows(r.data))
      .catch((e) => setError(errText(e, "Couldn't load distribution groups")))
      .finally(() => setLoading(false));
  }, [customerId, enabled]);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!enabled) return;
    api
      .get(`/api/v1/m365/${customerId}/exchange/accepted-domains`)
      .then((r) => {
        const list: string[] = (r.data || [])
          .map((d: any) => d.DomainName)
          .filter(Boolean);
        setDomains(list);
        // Default to the tenant's default domain, which is what Exchange would
        // have picked anyway — so the common case needs no interaction.
        const def = (r.data || []).find((d: any) => d.Default)?.DomainName;
        setDomain(def || list[0] || "");
      })
      .catch(() => {
        // Non-fatal: fall back to letting Exchange derive the address, which is
        // exactly the old behaviour.
        setDomains([]);
        setDomain("");
      });
  }, [customerId, enabled]);

  const saveAddress = async (identity: string) => {
    const addr = editAddr.trim();
    if (!addr.includes("@")) {
      setError("Enter a full email address, e.g. sales@contoso.com");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.put(
        `/api/v1/m365/${customerId}/exchange/distribution-groups/${encodeURIComponent(identity)}/address`,
        { primary_smtp_address: addr },
      );
      setEditing(null);
      setEditAddr("");
      load();
    } catch (e) {
      setError(errText(e, "Couldn't change the address"));
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!name.trim() || !alias.trim()) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/v1/m365/${customerId}/exchange/distribution-groups`, {
        name: name.trim(),
        alias: alias.trim(),
        mail_enabled_security: mailSec,
        // Only send an address when we actually resolved a domain; otherwise
        // let Exchange derive it, as before.
        ...(domain ? { primary_smtp_address: `${alias.trim()}@${domain}` } : {}),
      });
      setName("");
      setAlias("");
      load();
    } catch (e) {
      setError(errText(e, "Create failed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (identity: string) => {
    if (!window.confirm(`Delete "${identity}"?`)) return;
    setError("");
    try {
      await api.delete(
        `/api/v1/m365/${customerId}/exchange/distribution-groups/${encodeURIComponent(identity)}`,
      );
      if (open === identity) setOpen(null);
      load();
    } catch (e) {
      setError(errText(e, "Delete failed"));
    }
  };

  const loadMembers = (identity: string) => {
    setMembersLoading(true);
    setMembers([]);
    api
      .get(`/api/v1/m365/${customerId}/exchange/distribution-groups/${encodeURIComponent(identity)}/members`)
      .then((r) => setMembers(r.data))
      .catch((e) => setError(errText(e, "Couldn't load members")))
      .finally(() => setMembersLoading(false));
  };
  const toggle = (identity: string) => {
    if (open === identity) {
      setOpen(null);
      return;
    }
    setOpen(identity);
    setNewMember("");
    loadMembers(identity);
  };
  const addMember = async (identity: string) => {
    if (!newMember.trim()) return;
    setError("");
    try {
      await api.post(
        `/api/v1/m365/${customerId}/exchange/distribution-groups/${encodeURIComponent(identity)}/members`,
        { member: newMember.trim() },
      );
      setNewMember("");
      loadMembers(identity);
    } catch (e) {
      setError(errText(e, "Add member failed"));
    }
  };
  const removeMember = async (identity: string, member: string) => {
    setError("");
    try {
      await api.post(
        `/api/v1/m365/${customerId}/exchange/distribution-groups/${encodeURIComponent(identity)}/members/remove`,
        { member },
      );
      loadMembers(identity);
    } catch (e) {
      setError(errText(e, "Remove member failed"));
    }
  };

  return (
    <div className={`${CARD} p-4`}>
      <h3 className={H}>Distribution lists &amp; mail-enabled security groups</h3>
      <p className={`${SUB} mb-3`}>
        Classic Exchange recipients — distinct from Microsoft 365 groups and from shared mailboxes.
        Click one to manage its members.
      </p>
      {!enabled ? (
        <p className={`${SUB} italic`}>Enable Exchange Online management to create these.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Sales)" className={`${INPUT} w-48`} />
            <input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Alias (e.g. sales)" className={`${INPUT} w-40`} />
            {domains.length > 0 && (
              <>
                <span className="text-xs text-gray-400 dark:text-gray-500">@</span>
                <select
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className={`${INPUT} w-52`}
                  title="Which accepted domain the address is created on"
                >
                  {domains.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </>
            )}
            <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={mailSec} onChange={(e) => setMailSec(e.target.checked)} />
              Mail-enabled security group
            </label>
            <button onClick={create} disabled={busy || !name.trim() || !alias.trim()} className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {busy ? "Creating…" : "Create"}
            </button>
          </div>
          {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}
          {loading ? (
            <p className={`${SUB} py-3 flex items-center gap-2`}>
              <span className="inline-block h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
              Loading from Exchange Online…
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.length === 0 && <p className={`${SUB} py-2`}>None yet.</p>}
              {rows.map((g, i) => {
                const id = g.PrimarySmtpAddress || g.Name;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between py-1.5 text-sm text-gray-700 dark:text-gray-200">
                      <button onClick={() => toggle(id)} className="flex-1 text-left hover:underline" title={id}>
                        {g.DisplayName || g.Name}
                        {g.PrimarySmtpAddress && <span className={`${SUB} ml-2`}>{g.PrimarySmtpAddress}</span>}
                        {g.RecipientTypeDetails && <span className={`${SUB} ml-2`}>({g.RecipientTypeDetails})</span>}
                        <span className="ml-2 text-gray-400 dark:text-gray-500">{open === id ? "▲" : "▼"}</span>
                      </button>
                      {/* Edit the address in place. Before this the only way
                          to change a list's address was to delete it and start
                          again, which threw away its membership. */}
                      {editing === id ? (
                        <span className="flex items-center gap-1 ml-3">
                          <input
                            value={editAddr}
                            onChange={(e) => setEditAddr(e.target.value)}
                            placeholder="sales@contoso.com"
                            className={`${INPUT} w-56`}
                            autoFocus
                          />
                          <button
                            onClick={() => saveAddress(id)}
                            disabled={busy}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditing(null);
                              setEditAddr("");
                            }}
                            className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => {
                            setEditing(id);
                            setEditAddr(g.PrimarySmtpAddress || "");
                          }}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline ml-3"
                          title="Change this list's email address"
                        >
                          Edit address
                        </button>
                      )}
                      <button onClick={() => remove(id)} className="text-xs text-red-600 dark:text-red-400 hover:underline ml-3">
                        Delete
                      </button>
                    </div>
                    {open === id && (
                      <div className="pl-4 pb-2 bg-gray-50/50 dark:bg-gray-900/40 rounded">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase py-1">
                          Members
                        </div>
                        {membersLoading ? (
                          <p className={`${SUB} py-1`}>Loading members…</p>
                        ) : members.length === 0 ? (
                          <p className={`${SUB} py-1`}>No members.</p>
                        ) : (
                          members.map((m, j) => (
                            <div key={j} className="flex items-center justify-between text-sm py-0.5 text-gray-700 dark:text-gray-200">
                              <span>
                                {m.DisplayName}
                                {m.PrimarySmtpAddress && <span className={`${SUB} ml-2`}>{m.PrimarySmtpAddress}</span>}
                              </span>
                              <button
                                onClick={() => removeMember(id, m.PrimarySmtpAddress || m.DisplayName)}
                                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            value={newMember}
                            onChange={(e) => setNewMember(e.target.value)}
                            placeholder="member email to add"
                            className={`${INPUT} flex-1`}
                          />
                          <button
                            onClick={() => addMember(id)}
                            disabled={!newMember.trim()}
                            className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Shared mailboxes (EXO) ───────────────────────────────────────────────────

function SharedMailboxes({
  customerId,
  users,
  enabled,
}: {
  customerId: number;
  users: M365User[];
  enabled: boolean;
}) {
  const [rows, setRows] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [alias, setAlias] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [perms, setPerms] = useState<{ full_access: any[]; send_as: any[] } | null>(null);
  const [aliasData, setAliasData] = useState<{ primary?: string | null; aliases: string[] }>({
    aliases: [],
  });
  const [detailLoading, setDetailLoading] = useState(false);
  const [grantUser, setGrantUser] = useState("");
  const [grantKind, setGrantKind] = useState<"full_access" | "send_as">("full_access");

  const tenantUsers = users.filter((u) => u.customer_id === customerId).sort(byName);

  const load = useCallback(() => {
    if (!enabled) return;
    setLoading(true);
    api
      .get(`/api/v1/m365/${customerId}/exchange/shared-mailboxes`)
      .then((r) => setRows(r.data))
      .catch((e) => setError(errText(e, "Couldn't load shared mailboxes")))
      .finally(() => setLoading(false));
  }, [customerId, enabled]);
  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!name.trim() || !alias.trim()) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/v1/m365/${customerId}/exchange/shared-mailboxes`, {
        name: name.trim(),
        alias: alias.trim(),
      });
      setName("");
      setAlias("");
      load();
    } catch (e) {
      setError(errText(e, "Create failed"));
    } finally {
      setBusy(false);
    }
  };

  const loadDetail = (addr: string) => {
    setDetailLoading(true);
    setPerms(null);
    setAliasData({ aliases: [] });
    Promise.allSettled([
      api.get(`/api/v1/m365/${customerId}/exchange/mailbox-permissions`, { params: { mailbox: addr } }),
      api.get(`/api/v1/m365/${customerId}/mailbox/${encodeURIComponent(addr)}/settings`),
    ])
      .then(([p, s]) => {
        if (p.status === "fulfilled") setPerms(p.value.data);
        if (s.status === "fulfilled") setAliasData(s.value.data?.aliases || { aliases: [] });
      })
      .finally(() => setDetailLoading(false));
  };
  const toggle = (addr: string) => {
    if (open === addr) {
      setOpen(null);
      return;
    }
    setOpen(addr);
    setGrantUser("");
    loadDetail(addr);
  };
  const grant = async (addr: string) => {
    if (!grantUser) return;
    try {
      await api.post(`/api/v1/m365/${customerId}/exchange/mailbox-permissions`, {
        mailbox: addr,
        user: grantUser,
        kind: grantKind,
      });
      setGrantUser("");
      loadDetail(addr);
    } catch (e) {
      setError(errText(e, "Grant failed"));
    }
  };
  const revoke = async (addr: string, u: string, k: "full_access" | "send_as") => {
    try {
      await api.post(`/api/v1/m365/${customerId}/exchange/mailbox-permissions/remove`, {
        mailbox: addr,
        user: u,
        kind: k,
      });
      loadDetail(addr);
    } catch (e) {
      setError(errText(e, "Revoke failed"));
    }
  };

  return (
    <div className={`${CARD} p-4`}>
      <h3 className={H}>Shared mailboxes</h3>
      <p className={`${SUB} mb-3`}>
        A mailbox multiple people open — distinct from a distribution list. Click one to manage its
        delegation and see aliases.
      </p>
      {!enabled ? (
        <p className={`${SUB} italic`}>Enable Exchange Online management to create these.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. Support)" className={`${INPUT} w-48`} />
            <input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Alias (e.g. support)" className={`${INPUT} w-40`} />
            <button onClick={create} disabled={busy || !name.trim() || !alias.trim()} className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {busy ? "Creating…" : "Create"}
            </button>
          </div>
          {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}
          {loading ? (
            <p className={`${SUB} py-3 flex items-center gap-2`}>
              <span className="inline-block h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
              Loading from Exchange Online…
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.length === 0 && <p className={`${SUB} py-2`}>None yet.</p>}
              {rows.map((m, i) => {
                const addr = m.PrimarySmtpAddress || m.Alias;
                return (
                  <div key={i}>
                    <button
                      onClick={() => toggle(addr)}
                      className="w-full flex items-center justify-between py-1.5 text-sm text-gray-700 dark:text-gray-200 text-left hover:underline"
                      title={addr}
                    >
                      <span>
                        {m.DisplayName}
                        {m.PrimarySmtpAddress && <span className={`${SUB} ml-2`}>{m.PrimarySmtpAddress}</span>}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">{open === addr ? "▲" : "▼"}</span>
                    </button>
                    {open === addr && (
                      <div className="pl-4 pb-2 bg-gray-50/50 dark:bg-gray-900/40 rounded text-sm">
                        {detailLoading ? (
                          <p className={`${SUB} py-1`}>Loading…</p>
                        ) : (
                          <>
                            <div className="py-1">
                              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                                Aliases
                              </div>
                              <AliasManager
                                customerId={customerId}
                                mailbox={addr}
                                primary={aliasData.primary}
                                aliases={aliasData.aliases}
                                onChange={setAliasData}
                              />
                            </div>
                            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase pt-2">
                              Delegation
                            </div>
                            {(perms?.full_access || []).map((p, j) => (
                              <div key={`f${j}`} className="flex items-center justify-between py-0.5 text-gray-700 dark:text-gray-200">
                                <span>{p.User} <span className={SUB}>Full Access</span></span>
                                <button onClick={() => revoke(addr, p.User, "full_access")} className="text-xs text-red-600 dark:text-red-400 hover:underline">Revoke</button>
                              </div>
                            ))}
                            {(perms?.send_as || []).map((p, j) => (
                              <div key={`s${j}`} className="flex items-center justify-between py-0.5 text-gray-700 dark:text-gray-200">
                                <span>{p.Trustee} <span className={SUB}>Send As</span></span>
                                <button onClick={() => revoke(addr, p.Trustee, "send_as")} className="text-xs text-red-600 dark:text-red-400 hover:underline">Revoke</button>
                              </div>
                            ))}
                            {!(perms?.full_access || []).length && !(perms?.send_as || []).length && (
                              <p className={`${SUB} py-0.5`}>No delegation.</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <select value={grantUser} onChange={(e) => setGrantUser(e.target.value)} className={`${INPUT} flex-1`}>
                                <option value="">Grant to…</option>
                                {tenantUsers.map((u) => (
                                  <option key={u.id} value={u.upn}>{u.display_name || u.upn}</option>
                                ))}
                              </select>
                              <select value={grantKind} onChange={(e) => setGrantKind(e.target.value as any)} className={INPUT}>
                                <option value="full_access">Full Access</option>
                                <option value="send_as">Send As</option>
                              </select>
                              <button onClick={() => grant(addr)} disabled={!grantUser} className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                Grant
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Mailbox permissions: FullAccess + SendAs (EXO) ───────────────────────────

function MailboxPermissions({
  customerId,
  users,
  enabled,
}: {
  customerId: number;
  users: M365User[];
  enabled: boolean;
}) {
  const [mailbox, setMailbox] = useState("");
  const [user, setUser] = useState("");
  const [kind, setKind] = useState<"full_access" | "send_as">("full_access");
  const [perms, setPerms] = useState<{ full_access: any[]; send_as: any[] } | null>(null);
  const [shared, setShared] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const tenantUsers = users.filter((u) => u.customer_id === customerId).sort(byName);

  // Selectable mailboxes = user mailboxes + shared mailboxes.
  useEffect(() => {
    if (!enabled) return;
    api
      .get(`/api/v1/m365/${customerId}/exchange/shared-mailboxes`)
      .then((r) => setShared(r.data))
      .catch(() => setShared([]));
  }, [enabled, customerId]);

  const mailboxOptions = [
    ...tenantUsers.map((u) => ({ value: u.upn, label: `${u.display_name || u.upn} (user)` })),
    ...shared
      .filter((s) => s.PrimarySmtpAddress)
      .map((s) => ({ value: s.PrimarySmtpAddress, label: `${s.DisplayName || s.PrimarySmtpAddress} (shared)` })),
  ].sort((a, b) => a.label.localeCompare(b.label));

  const load = async () => {
    if (!mailbox.trim()) return;
    setError("");
    setLoading(true);
    try {
      const r = await api.get(`/api/v1/m365/${customerId}/exchange/mailbox-permissions`, {
        params: { mailbox: mailbox.trim() },
      });
      setPerms(r.data);
    } catch (e) {
      setError(errText(e, "Couldn't load permissions"));
    } finally {
      setLoading(false);
    }
  };

  const grant = async () => {
    if (!mailbox.trim() || !user.trim()) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/v1/m365/${customerId}/exchange/mailbox-permissions`, {
        mailbox: mailbox.trim(),
        user: user.trim(),
        kind,
      });
      setUser("");
      load();
    } catch (e) {
      setError(errText(e, "Grant failed"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (u: string, k: "full_access" | "send_as") => {
    setError("");
    try {
      await api.post(`/api/v1/m365/${customerId}/exchange/mailbox-permissions/remove`, {
        mailbox: mailbox.trim(),
        user: u,
        kind: k,
      });
      load();
    } catch (e) {
      setError(errText(e, "Revoke failed"));
    }
  };

  return (
    <div className={`${CARD} p-4`}>
      <h3 className={H}>Mailbox permissions</h3>
      <p className={`${SUB} mb-3`}>Grant Full Access or Send As on a mailbox (delegation).</p>
      {!enabled ? (
        <p className={`${SUB} italic`}>Enable Exchange Online management to manage delegation.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select
              value={mailbox}
              onChange={(e) => {
                setMailbox(e.target.value);
                setPerms(null);
              }}
              className={`${INPUT} w-72`}
            >
              <option value="">Select a mailbox…</option>
              {mailboxOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <button onClick={load} disabled={!mailbox.trim() || loading} className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-lg disabled:opacity-50">
              {loading ? "Loading…" : "Load"}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select value={user} onChange={(e) => setUser(e.target.value)} className={`${INPUT} w-56`}>
              <option value="">Grant to…</option>
              {tenantUsers.map((u) => (
                <option key={u.id} value={u.upn}>
                  {u.display_name || u.upn}
                </option>
              ))}
            </select>
            <select value={kind} onChange={(e) => setKind(e.target.value as "full_access" | "send_as")} className={INPUT}>
              <option value="full_access">Full Access</option>
              <option value="send_as">Send As</option>
            </select>
            <button onClick={grant} disabled={busy || !mailbox.trim() || !user.trim()} className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {busy ? "Granting…" : "Grant"}
            </button>
          </div>
          {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}
          {loading ? (
            <p className={`${SUB} py-3 flex items-center gap-2`}>
              <span className="inline-block h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
              Loading permissions from Exchange Online…
            </p>
          ) : perms ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <div className={`${SUB} uppercase mb-1`}>Full Access</div>
                {(perms.full_access || []).length === 0 ? (
                  <p className={`${SUB}`}>No one has Full Access.</p>
                ) : (
                  perms.full_access.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-0.5 text-gray-700 dark:text-gray-200">
                      <span>
                        {p.User}
                        <span className={`${SUB} ml-2`}>
                          {Array.isArray(p.AccessRights) ? p.AccessRights.join(", ") : p.AccessRights || "FullAccess"}
                        </span>
                      </span>
                      <button onClick={() => revoke(p.User, "full_access")} className="text-xs text-red-600 dark:text-red-400 hover:underline">
                        Revoke
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div>
                <div className={`${SUB} uppercase mb-1`}>Send As</div>
                {(perms.send_as || []).length === 0 ? (
                  <p className={`${SUB}`}>No one can Send As.</p>
                ) : (
                  perms.send_as.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-0.5 text-gray-700 dark:text-gray-200">
                      <span>
                        {p.Trustee}
                        <span className={`${SUB} ml-2`}>
                          {Array.isArray(p.AccessRights) ? p.AccessRights.join(", ") : p.AccessRights || "SendAs"}
                        </span>
                      </span>
                      <button onClick={() => revoke(p.Trustee, "send_as")} className="text-xs text-red-600 dark:text-red-400 hover:underline">
                        Revoke
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <p className={`${SUB} italic`}>Select a mailbox and click Load to see who has access.</p>
          )}
        </>
      )}
    </div>
  );
}

// ── Mailbox settings: auto-reply + forwarding (Graph) ────────────────────────

function MailboxSettingsSection({ customerId, users }: { customerId: number; users: M365User[] }) {
  const [userId, setUserId] = useState("");
  const [settings, setSettings] = useState<MailboxSettings | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [arStatus, setArStatus] = useState("disabled");
  const [arInternal, setArInternal] = useState("");
  const [arExternal, setArExternal] = useState("");
  const [fwdTo, setFwdTo] = useState("");
  const [keepCopy, setKeepCopy] = useState(true);
  const [aliasData, setAliasData] = useState<{ primary?: string | null; aliases: string[] }>({
    aliases: [],
  });
  // EVERY forward on this mailbox, across both mechanisms.
  //
  // Exchange forwards two independent ways and neither vendor UI shows the
  // other: an inbox RULE (what this tool sets, because Graph v1.0 has no
  // mailbox-forwarding property) and the mailbox PROPERTY (what the Microsoft
  // 365 admin center edits). This panel previously read only the property, so
  // it reported "No mailbox forwarding set" while our own rule was forwarding.
  const [fwdOverview, setFwdOverview] = useState<{
    forwards: {
      mechanism: string;
      mechanism_label: string;
      forward_to: string | null;
      enabled: boolean;
      keep_copy: boolean;
      rule_name: string | null;
      managed_by_platform: boolean;
      editable_here: boolean;
      note: string;
    }[];
    active_count: number;
    distinct_destinations: string[];
    conflict: { severity: string; summary: string; detail: string } | null;
    unreadable: string[];
    complete: boolean;
  } | null>(null);
  const [exoFwdLoading, setExoFwdLoading] = useState(false);

  const tenantUsers = users.filter((u) => u.customer_id === customerId).sort(byName);

  const fetchExoFwd = () => {
    if (!userId) return;
    setExoFwdLoading(true);
    api
      .get(`/api/v1/m365/${customerId}/exchange/forwarding-overview`, {
        params: { mailbox: userId },
      })
      .then((r) => setFwdOverview(r.data))
      // On failure show nothing rather than a false "not forwarding" — the
      // endpoint already reports partial reads via `unreadable`.
      .catch(() => setFwdOverview(null))
      .finally(() => setExoFwdLoading(false));
  };

  const loadSettings = () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    setNotice("");
    setSettings(null);
    setFwdOverview(null);
    api
      .get(`/api/v1/m365/${customerId}/mailbox/${encodeURIComponent(userId)}/settings`)
      .then((r) => {
        const s: MailboxSettings = r.data;
        setSettings(s);
        setArStatus(s.automatic_replies.status || "disabled");
        setArInternal(s.automatic_replies.internal_message || "");
        setArExternal(s.automatic_replies.external_message || "");
        setFwdTo(s.forwarding.forward_to || "");
        setKeepCopy(s.forwarding.keep_copy);
        setAliasData(s.aliases || { aliases: [] });
      })
      .catch((e) => setError(errText(e, "Couldn't load mailbox settings")))
      .finally(() => setLoading(false));
    fetchExoFwd();
  };

  const removeExoForwarding = async () => {
    if (!userId) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api.post(`/api/v1/m365/${customerId}/exchange/mailbox-forwarding/remove`, {
        mailbox: userId,
      });
      setNotice("Mailbox forwarding removed.");
      fetchExoFwd();
    } catch (e) {
      setError(errText(e, "Couldn't remove forwarding"));
    } finally {
      setBusy(false);
    }
  };

  const saveAutoReply = async () => {
    if (!userId) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api.post(`/api/v1/m365/${customerId}/mailbox/${encodeURIComponent(userId)}/autoreply`, {
        status: arStatus,
        internal_message: arInternal,
        external_message: arExternal,
      });
      setNotice("Auto-reply updated.");
      loadSettings();
    } catch (e) {
      setError(errText(e, "Update failed"));
    } finally {
      setBusy(false);
    }
  };

  /** Delete the inbox rule this tool manages.
   *
   * Separate from the mailbox-property Remove button next to it, because they
   * are genuinely different mechanisms — clearing one does not touch the other,
   * which is exactly how a forward survived being "changed" in the Microsoft 365
   * admin center and kept delivering to the wrong person.
   */
  const removeRuleForwarding = async () => {
    if (!userId) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      // An empty forward_to deletes the rule server-side.
      await api.post(
        `/api/v1/m365/${customerId}/mailbox/${encodeURIComponent(userId)}/forwarding`,
        { forward_to: "", keep_copy: true },
      );
      setFwdTo("");
      setNotice("Forwarding rule removed.");
      loadSettings();
    } catch (e) {
      setError(errText(e, "Couldn't remove the forwarding rule"));
    } finally {
      setBusy(false);
    }
  };

  const saveForwarding = async () => {
    if (!userId) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api.post(`/api/v1/m365/${customerId}/mailbox/${encodeURIComponent(userId)}/forwarding`, {
        forward_to: fwdTo,
        keep_copy: keepCopy,
      });
      setNotice(fwdTo ? "Forwarding enabled." : "Forwarding disabled.");
      loadSettings();
    } catch (e) {
      setError(errText(e, "Update failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${CARD} p-4`}>
      <h3 className={`${H} mb-2`}>Mailbox settings</h3>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          value={tenantUsers.some((u) => u.upn === userId) ? userId : ""}
          onChange={(e) => setUserId(e.target.value)}
          className={`${INPUT}`}
        >
          <option value="">Pick a mailbox…</option>
          {tenantUsers.map((u) => (
            <option key={u.id} value={u.upn}>
              {u.display_name || u.upn}
            </option>
          ))}
        </select>
        <input type="text" placeholder="…or type a UPN" value={userId} onChange={(e) => setUserId(e.target.value)} className={`${INPUT} w-56`} />
        <button onClick={loadSettings} disabled={!userId} className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
          Load
        </button>
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}
      {notice && <p className="text-xs text-green-700 dark:text-green-400 mb-2">{notice}</p>}
      {loading ? (
        <p className={`${SUB} flex items-center gap-2`}>
          <span className="inline-block h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
          Loading mailbox settings…
        </p>
      ) : settings ? (
        <>
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
              Email aliases
            </div>
            <AliasManager
              customerId={customerId}
              mailbox={userId}
              primary={aliasData.primary}
              aliases={aliasData.aliases}
              onChange={setAliasData}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
          <div>
            <h4 className={`${H} mb-2`}>Automatic replies</h4>
            <label className={`block ${SUB} mb-1`}>Status</label>
            <select value={arStatus} onChange={(e) => setArStatus(e.target.value)} className={`${INPUT} w-full mb-3`}>
              {AUTOREPLY_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <label className={`block ${SUB} mb-1`}>Internal message</label>
            <textarea value={arInternal} onChange={(e) => setArInternal(e.target.value)} rows={3} className={`${INPUT} w-full mb-3`} />
            <label className={`block ${SUB} mb-1`}>External message</label>
            <textarea value={arExternal} onChange={(e) => setArExternal(e.target.value)} rows={3} className={`${INPUT} w-full mb-3`} />
            <button onClick={saveAutoReply} disabled={busy} className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              Save auto-reply
            </button>
          </div>
          <div>
            <h4 className={`${H} mb-1`}>Mail forwarding</h4>

            {/* Existing mailbox-level (EAC) forwarding — from Exchange Online. */}
            <div className="mb-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40 p-2">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                Currently forwarding
              </div>
              {exoFwdLoading ? (
                <p className={`${SUB} flex items-center gap-2`}>
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-500 animate-pulse" />
                  Checking Exchange…
                </p>
              ) : fwdOverview && fwdOverview.forwards.length > 0 ? (
                <div className="space-y-2">
                  {/* The conflict banner is the whole point: two mechanisms
                      forwarding to different people is invisible in both vendor
                      UIs and silently delivers mail to someone who should no
                      longer receive it. */}
                  {fwdOverview.conflict && (
                    <div
                      className={`rounded-lg px-3 py-2 text-xs ${
                        fwdOverview.conflict.severity === "critical"
                          ? "bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800"
                          : "bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                      }`}
                    >
                      <div className="font-semibold">{fwdOverview.conflict.summary}</div>
                      <div className="mt-0.5">{fwdOverview.conflict.detail}</div>
                    </div>
                  )}
                  {fwdOverview.forwards.map((f, i) => (
                    <div
                      key={`${f.mechanism}-${f.rule_name || i}`}
                      className="flex items-start justify-between gap-2"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-200">
                        → {f.forward_to}
                        {!f.enabled && (
                          <span className={`${SUB} ml-2`}>(disabled)</span>
                        )}
                        <span className={`${SUB} ml-2`}>
                          {f.keep_copy ? "keeps a copy" : "no copy kept"}
                        </span>
                        {/* Naming the mechanism is what makes the fix findable. */}
                        <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                          {f.mechanism_label}
                          {f.rule_name ? ` "${f.rule_name}"` : ""} — {f.note}
                        </span>
                      </span>
                      {f.mechanism === "mailbox_property" && (
                        <button
                          onClick={removeExoForwarding}
                          disabled={busy}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50 shrink-0"
                        >
                          Remove
                        </button>
                      )}
                      {f.mechanism === "inbox_rule" && f.managed_by_platform && (
                        <button
                          onClick={removeRuleForwarding}
                          disabled={busy}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50 shrink-0"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  {!fwdOverview.complete &&
                    fwdOverview.unreadable.map((u, i) => (
                      <p key={i} className="text-[11px] text-amber-700 dark:text-amber-400">
                        {u}
                      </p>
                    ))}
                </div>
              ) : fwdOverview && !fwdOverview.complete ? (
                // Never say "not forwarding" off a partial read — that is how a
                // live forward stays hidden.
                <div className="space-y-1">
                  <span className={SUB}>Forwarding could not be fully checked.</span>
                  {fwdOverview.unreadable.map((u, i) => (
                    <p key={i} className="text-[11px] text-amber-700 dark:text-amber-400">
                      {u}
                    </p>
                  ))}
                </div>
              ) : (
                <span className={SUB}>
                  Not forwarding — checked both inbox rules and the mailbox setting.
                </span>
              )}
            </div>

            <p className={`${SUB} mb-3`}>Forward via an inbox rule. Empty address = off.</p>
            <label className={`block ${SUB} mb-1`}>Forward to</label>
            <input type="text" value={fwdTo} onChange={(e) => setFwdTo(e.target.value)} placeholder="address@example.com" className={`${INPUT} w-full mb-3`} />
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 mb-3">
              <input type="checkbox" checked={keepCopy} onChange={(e) => setKeepCopy(e.target.checked)} />
              Keep a copy in the mailbox
            </label>
            <button onClick={saveForwarding} disabled={busy} className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              Save forwarding
            </button>
          </div>
          </div>
        </>
      ) : (
        <p className={`${SUB} italic`}>Pick a mailbox and click Load.</p>
      )}
    </div>
  );
}

// ── Message trace (EXO Get-MessageTrace) ─────────────────────────────────────

const TRACE_STATUSES = [
  "",
  "Delivered",
  "Failed",
  "Pending",
  "FilteredAsSpam",
  "Quarantined",
  "Expanded",
] as const;

function statusBadge(status?: string): string {
  const s = (status || "").toLowerCase();
  if (s === "delivered")
    return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
  if (s === "failed")
    return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
  if (s.includes("spam") || s === "quarantined")
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
}

function MessageTrace({ customerId, enabled }: { customerId: number; enabled: boolean }) {
  const [sender, setSender] = useState("");
  const [recipient, setRecipient] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { sort, toggle } = useSort("received", "desc");

  const traceSortVal = (m: any, key: string): string | number => {
    switch (key) {
      case "received":
        return m.received ? Date.parse(m.received) : 0;
      case "sender":
        return (m.sender || "").toLowerCase();
      case "recipient":
        return (m.recipient || "").toLowerCase();
      case "subject":
        return (m.subject || "").toLowerCase();
      case "status":
        return (m.status || "").toLowerCase();
      default:
        return 0;
    }
  };
  const sortedRows = rows ? sortRows(rows, sort, traceSortVal) : rows;

  const search = async () => {
    setLoading(true);
    setError("");
    setRows(null);
    try {
      const r = await api.get(`/api/v1/m365/${customerId}/exchange/message-trace`, {
        params: { sender, recipient, start, end, status },
      });
      setRows(r.data);
    } catch (e) {
      setError(errText(e, "Message trace failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${CARD} p-4`}>
      <h3 className={H}>Message trace</h3>
      <p className={`${SUB} mb-3`}>
        Track message delivery over the last 10 days (Exchange admin center's message trace). Leave
        fields blank for everything in the last 48 hours.
      </p>
      {!enabled ? (
        <p className={`${SUB} italic`}>Enable Exchange Online management to run a message trace.</p>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 mb-3">
            <div>
              <label className={`block ${SUB} mb-1`}>Sender</label>
              <input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="anyone" className={`${INPUT} w-full`} />
            </div>
            <div>
              <label className={`block ${SUB} mb-1`}>Recipient</label>
              <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="anyone" className={`${INPUT} w-full`} />
            </div>
            <div>
              <label className={`block ${SUB} mb-1`}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${INPUT} w-full`}>
                {TRACE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s || "Any status"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block ${SUB} mb-1`}>From</label>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={`${INPUT} w-full`} />
            </div>
            <div>
              <label className={`block ${SUB} mb-1`}>To</label>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={`${INPUT} w-full`} />
            </div>
            <div className="flex items-end">
              <button onClick={search} disabled={loading} className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 w-full">
                {loading ? "Tracing…" : "Search"}
              </button>
            </div>
          </div>
          {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}
          {loading ? (
            <p className={`${SUB} py-3 flex items-center gap-2`}>
              <span className="inline-block h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
              Querying Exchange Online (this can take a moment)…
            </p>
          ) : rows == null ? null : rows.length === 0 ? (
            <p className={`${SUB} py-2`}>No messages matched.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
              {/* NO SILENT CAP.
                *
                * The server runs Get-MessageTraceV2 with -ResultSize 500.
                * Exchange itself warns when there is more ("There are more
                * results...") - and that warning, landing on stdout, used to
                * break the JSON and answer 502. Now that the warning is
                * silenced, the truncation would be invisible instead, which
                * is worse: a technician chasing a delivery problem would
                * conclude the message is not there.
                *
                * Exactly 500 rows means the cap was hit. Derived from the
                * row count rather than a new API field, so the array
                * contract this panel already relies on does not change. */}
              {(rows || []).length >= 500 && (
                <div className="px-3 py-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
                  Showing the first 500 messages — there are more. Narrow the
                  date range or add a sender/recipient to see the rest.
                </div>
              )}
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400 uppercase">
                  <tr>
                    <SortableTh label="Received" sortKey="received" sort={sort} onSort={toggle} />
                    <SortableTh label="From" sortKey="sender" sort={sort} onSort={toggle} />
                    <SortableTh label="To" sortKey="recipient" sort={sort} onSort={toggle} />
                    <SortableTh label="Subject" sortKey="subject" sort={sort} onSort={toggle} />
                    <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggle} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(sortedRows || []).map((m, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40" title={m.message_id}>
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {m.received ? formatDateTime(m.received) : "—"}
                      </td>
                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200 max-w-[14rem] truncate" title={m.sender}>
                        {m.sender}
                      </td>
                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200 max-w-[14rem] truncate" title={m.recipient}>
                        {m.recipient}
                      </td>
                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200 max-w-[20rem] truncate" title={m.subject}>
                        {m.subject || <span className={SUB}>(no subject)</span>}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(m.status)}`}>
                          {m.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
